import React from 'react';
import { AlertTriangle, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useSisConfig } from '../../contexts/SisConfigUi';

/**
 * SqlexecConfirmDialog - Modal de confirmación simple: ícono grande + pregunta + 2 botones
 * Zero-hardcode: colores desde botones_ui/modales_ui (sisconfig)
 *
 * @param {boolean} isOpen - Estado del modal (abierto/cerrado)
 * @param {Function} onClose - Callback al cerrar/cancelar
 * @param {Function} onConfirm - Callback al confirmar
 * @param {string} tipo - 'danger' | 'success' | 'warning' | 'info'
 * @param {string} pregunta - Texto de la pregunta, ya resuelto (sin placeholders)
 * @param {string} confirmButton - Texto del botón de acción (ej. "Cancelar factura")
 * @param {string} cancelButton - Texto del botón secundario
 * @param {boolean} isSaving - Estado de guardado en progreso
 */

// Mapa tipo -> ícono. "danger", "warning" y "success" resueltos; "info"
// se agrega aquí cuando se necesite.
const TIPO_ICONOS = {
  danger: AlertTriangle,
  warning: AlertCircle,
  success: CheckCircle,
};

// botones_ui solo trae el color sólido del botón, no un tono pastel de fondo para
// el badge -- se deriva ese tinte a partir del mismo color en vez de hardcodear un hex nuevo.
const hexToRgba = (hex, alpha) => {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return `rgba(220, 38, 38, ${alpha})`;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SqlexecConfirmDialog = ({
  isOpen = false,
  onClose,
  onConfirm,
  tipo = 'danger',
  pregunta,
  confirmButton = 'Confirmar',
  cancelButton = 'Cancelar',
  isSaving = false
}) => {

  const { modales_ui, botones_ui } = useSisConfig();

  if (!isOpen) return null;

  const Icon = TIPO_ICONOS[tipo] || TIPO_ICONOS.danger;
  const btnTipo = botones_ui?.[tipo] || botones_ui?.danger || {};
  const iconColor = btnTipo.backgroundColor || '#dc2626';
  const iconBg = hexToRgba(iconColor, 0.12);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      onClick={isSaving ? null : onClose}
      style={{
        backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.6)',
        backdropFilter: modales_ui?.overlay?.backdropBlur ? `blur(${modales_ui.overlay.backdropBlur})` : 'blur(4px)'
      }}
    >
      {/* Container */}
      <div
        className="w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: modales_ui?.container?.backgroundColor || '#ffffff',
          borderRadius: modales_ui?.container?.borderRadius || '12px',
          boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: modales_ui?.container?.border || '1px solid rgba(0, 0, 0, 0.05)',
          maxWidth: '28rem',
          overflow: 'hidden'
        }}
      >
        {/* Content: ícono grande + pregunta */}
        <div
          className="text-center"
          style={{
            padding: modales_ui?.content?.padding || '2rem',
            paddingTop: modales_ui?.content?.paddingTop || '2rem',
            paddingBottom: modales_ui?.content?.paddingBottom || '2rem'
          }}
        >
          <div
            className="mx-auto flex items-center justify-center mb-4"
            style={{
              width: '88px',
              height: '88px',
              backgroundColor: iconBg,
              borderRadius: '50%'
            }}
          >
            <Icon size={48} style={{ color: iconColor }} />
          </div>

          <h3
            className="font-semibold"
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#111827',
              fontFamily: 'inherit'
            }}
          >
            {pregunta}
          </h3>
        </div>

        {/* Footer */}
        <div
          className="flex"
          style={{
            paddingLeft: modales_ui?.footer?.paddingX || '2rem',
            paddingRight: modales_ui?.footer?.paddingX || '2rem',
            paddingTop: modales_ui?.footer?.paddingY || '1.25rem',
            paddingBottom: modales_ui?.footer?.paddingBottom || modales_ui?.footer?.paddingY || '1.25rem',
            borderTop: modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
            backgroundColor: modales_ui?.footer?.backgroundColor || '#fafafa',
            gap: modales_ui?.footer?.gap || '0.75rem',
            justifyContent: 'center'
          }}
        >
          {/* Botón Cancelar */}
          <button
            onClick={onClose}
            disabled={isSaving}
            className="transition-colors"
            style={{
              height: botones_ui?.height || '36px',
              fontSize: botones_ui?.fontSize || '0.875rem',
              fontFamily: 'inherit',
              fontWeight: botones_ui?.fontWeight || '500',
              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
              paddingTop: botones_ui?.padding?.y || '0.5rem',
              paddingBottom: botones_ui?.padding?.y || '0.5rem',
              borderRadius: botones_ui?.borderRadius || '6px',
              borderWidth: botones_ui?.borderWidth || '1px',
              borderStyle: 'solid',
              borderColor: botones_ui?.secondary?.borderColor || '#d1d5db',
              backgroundColor: botones_ui?.secondary?.backgroundColor || '#ffffff',
              color: botones_ui?.secondary?.textColor || '#374151',
              transitionDuration: botones_ui?.transitionDuration || '150ms',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? '0.5' : '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.target.style.backgroundColor = botones_ui?.secondary?.hoverBackgroundColor || '#f9fafb';
                e.target.style.color = botones_ui?.secondary?.hoverTextColor || '#111827';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.target.style.backgroundColor = botones_ui?.secondary?.backgroundColor || '#ffffff';
                e.target.style.color = botones_ui?.secondary?.textColor || '#374151';
              }
            }}
          >
            {cancelButton}
          </button>

          {/* Botón de acción (color según tipo) */}
          <button
            onClick={onConfirm}
            disabled={isSaving}
            className="transition-colors"
            style={{
              height: botones_ui?.height || '36px',
              fontSize: botones_ui?.fontSize || '0.875rem',
              fontFamily: 'inherit',
              fontWeight: botones_ui?.fontWeight || '500',
              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
              paddingTop: botones_ui?.padding?.y || '0.5rem',
              paddingBottom: botones_ui?.padding?.y || '0.5rem',
              borderRadius: botones_ui?.borderRadius || '6px',
              borderWidth: botones_ui?.borderWidth || '1px',
              borderStyle: 'solid',
              borderColor: isSaving
                ? (btnTipo.disabledBorderColor || '#9ca3af')
                : (btnTipo.borderColor || iconColor),
              backgroundColor: isSaving
                ? (btnTipo.disabledBackgroundColor || '#9ca3af')
                : (btnTipo.backgroundColor || iconColor),
              color: isSaving
                ? (btnTipo.disabledTextColor || '#ffffff')
                : (btnTipo.textColor || '#ffffff'),
              transitionDuration: botones_ui?.transitionDuration || '150ms',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.target.style.backgroundColor = btnTipo.hoverBackgroundColor || iconColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.target.style.backgroundColor = btnTipo.backgroundColor || iconColor;
              }
            }}
          >
            {isSaving ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Guardando...</span>
              </>
            ) : (
              confirmButton
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SqlexecConfirmDialog;
